import { Button, FlexBox, Icon, Page, Tooltip } from '@dixa/lib-shared-habla';
import {
  SettingsAudioDeleteCancelClickedProperties,
  SettingsAudioDeleteClickedProperties,
  SettingsAudioDeleteConfirmClickedProperties,
} from 'lib-shared-tracking';
import { ampli } from 'lib-shared-tracking';
import type { AsyncStatus, FlowUsage, WaitMusic } from 'lib-shared-types-api';

import type { Announcement } from 'apis/uploads';
import { uploadsApi } from 'apis/uploads';
import { waitMusicApi } from 'apis/waitMusic';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { FileUsagePageTable } from './components/table/table';
import { isAnnouncement } from './utils/typeguards/isAnnouncement';

type AudioType = 'wait-music' | 'announcement';

type Props = {
  type: AudioType;
  usage: readonly FlowUsage[];
  asyncStatus: AsyncStatus;
} & (
  | {
      type: 'wait-music';
      file?: WaitMusic;
    }
  | {
      type: 'announcement';
      file?: Announcement;
    }
);

export const FileUsagePage = ({
  type,
  usage,
  file,
  asyncStatus,
}: Props): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [deleteAnnouncement] = uploadsApi.useDeleteAnnouncementMutation();
  const [deleteWaitMusic] = waitMusicApi.useDeleteWaitMusicMutation();

  const name =
    (isAnnouncement(file) ? file?.pretty_name : file?.name) || 'Unnamed file';

  const redirectToAudioSettings = () => navigate({ path: '/settings/audio' });

  const handleDeleteAudioClick = async () => {
    if (!file) {
      return;
    }

    const analyticsPayload:
      | SettingsAudioDeleteClickedProperties
      | SettingsAudioDeleteConfirmClickedProperties
      | SettingsAudioDeleteCancelClickedProperties = {
      type,
      filename: name,
    };

    ampli.settingsAudioDeleteClicked(analyticsPayload);

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete audio message?',
            description: `Are you sure you want to delete the file "${name}"? Please note that this action can't be undone.`,
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      ampli.settingsAudioDeleteConfirmClicked(analyticsPayload);

      if (isAnnouncement(file)) {
        const [_, fileUUIDWithExtension] =
          file.url.match(/.+\/(.+\..{2,5})$/) || [];

        deleteAnnouncement({ file: fileUUIDWithExtension }).then(
          redirectToAudioSettings,
        );
      } else {
        deleteWaitMusic({ id: file.id }).then(redirectToAudioSettings);
      }
    } else {
      ampli.settingsAudioDeleteCancelClicked(analyticsPayload);
    }
  };

  const isDeletable = usage.length === 0 && asyncStatus === 'fulfilled';

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/audio" style={{ color: 'inherit' }}>
              Audio files
            </AppLink>

            <Icon name="angle-right" />

            {name}
          </FlexBox>
        }
      />

      <Page.Body>
        <Page.Section
          title="Flows using this audio file"
          controls={
            <>
              <Tooltip
                content="The audio file must be removed from all flows, before it can be deleted."
                disabled={isDeletable}
                wrap="span"
                dark
              >
                <Button
                  disabled={!isDeletable}
                  variant="danger"
                  onClick={handleDeleteAudioClick}
                >
                  Delete audio
                </Button>
              </Tooltip>
            </>
          }
        >
          <FileUsagePageTable
            type={type}
            filename={name}
            usage={usage}
            asyncStatus={asyncStatus}
          />
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
